import React, { useEffect } from 'react';
import { Typography } from '@mui/material';
import InputFluidRequirementIcon from '../../assets/icons/inputFluidRequirementIcon.svg';
import { BoxStyled, TypographyStyledHeader } from '../patientInformation/style';
import { ArcContainer } from '../../shared/components/arcContainer';
import { TabPanel } from '../../shared/components/tabPanel';
import { TypographyH4Bold } from '../estimatingRequirements/paedFluid/style';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import { useDispatch } from 'react-redux';
import {
  setRniData,
  setRniP1VolumeOffset,
  setRniP2VolumeOffset
} from '../../store/slices/metaDataSlice';
import { NavigationButtons } from '../../shared/components/navigationButtons';
import { BottleView } from '../compare/bottleView';
import { MlView } from '../compare/mLView';

const InputScreen = () => {
  const dispatch = useDispatch();
  const { rni } = useAppSelector(metaDataSelector);
  const {
    selectedProducts,
    bottleInputP1,
    bottleInputP2,
    volumeInputP1,
    volumeInputP2,
    p1VolumeCalculationOffset,
    p2VolumeCalculationOffset
  } = rni;

  useEffect(() => {
    // Calculate p1VolumeCalculationOffset
    if (volumeInputP1) {
      dispatch(setRniP1VolumeOffset(volumeInputP1));
    } else if (bottleInputP1) {
      const perBottleVol = selectedProducts[0].volumeOffset;
      const totalVol = bottleInputP1 * perBottleVol;
      dispatch(setRniP1VolumeOffset(totalVol));
    }
  }, [volumeInputP1, bottleInputP1]);

  useEffect(() => {
    // Calculate p2VolumeCalculationOffset
    if (volumeInputP2) {
      dispatch(setRniP2VolumeOffset(volumeInputP2));
    } else if (bottleInputP2) {
      const perBottleVol = selectedProducts[1].volumeOffset;
      const totalVol = bottleInputP2 * perBottleVol;
      dispatch(setRniP2VolumeOffset(totalVol));
    }
  }, [volumeInputP2, bottleInputP2]);

  const isNextDisabled = (): boolean => {
    if (selectedProducts.length === 1 && p1VolumeCalculationOffset) {
      return false;
    }

    if (
      selectedProducts.length > 1 &&
      p1VolumeCalculationOffset &&
      p2VolumeCalculationOffset
    ) {
      return false;
    }

    return true;
  };

  return (
    <>
      <BoxStyled>
        <TypographyStyledHeader>{`PRODUCTS`}</TypographyStyledHeader>

        <ArcContainer icon={InputFluidRequirementIcon} customizedHeight="auto">
          <TypographyH4Bold sx={{ textAlign: 'center' }} className="pt-16">
            {selectedProducts.length > 0 ? selectedProducts[0].name : ''}
            <Typography sx={{ fontFamily: 'CalibriItalic', fontSize: '1rem' }}>
              {selectedProducts.length > 0
                ? `${selectedProducts[0].category} - ${selectedProducts[0].volume}`
                : ''}
            </Typography>
          </TypographyH4Bold>

          <TabPanel
            tabs={[
              selectedProducts.length > 0 && selectedProducts[0].typeofcategory,
              'mL'
            ]}
            panelData={[
              <BottleView
                setBottleInput={(val) => {
                  dispatch(
                    setRniData({
                      ...rni,
                      bottleInputP1: val
                    })
                  );
                }}
                key={0}
                value={bottleInputP1}
                typeofcategory={
                  selectedProducts.length > 0
                    ? selectedProducts[0].typeofcategory
                    : 'Bottle'
                }
              />,
              <MlView
                setMlInput={(val) => {
                  dispatch(
                    setRniData({
                      ...rni,
                      volumeInputP1: val
                    })
                  );
                }}
                key={1}
                value={volumeInputP1}
              />
            ]}
          />
        </ArcContainer>

        {selectedProducts.length > 1 && (
          <ArcContainer
            icon={InputFluidRequirementIcon}
            customizedHeight="auto"
          >
            <TypographyH4Bold sx={{ textAlign: 'center' }} className="pt-16">
              {selectedProducts.length > 1 ? selectedProducts[1].name : ''}
              <Typography
                sx={{ fontFamily: 'CalibriItalic', fontSize: '1rem' }}
              >
                {selectedProducts.length > 1
                  ? `${selectedProducts[1].category} - ${selectedProducts[1].volume}`
                  : ''}
              </Typography>
            </TypographyH4Bold>

            <TabPanel
              tabs={[
                selectedProducts.length > 1 &&
                  selectedProducts[1].typeofcategory,
                'mL'
              ]}
              panelData={[
                <BottleView
                  setBottleInput={(val) => {
                    dispatch(
                      setRniData({
                        ...rni,
                        bottleInputP2: val
                      })
                    );
                  }}
                  key={0}
                  value={bottleInputP2}
                  typeofcategory={
                    selectedProducts.length > 1
                      ? selectedProducts[1].typeofcategory
                      : 'Bottle'
                  }
                />,
                <MlView
                  setMlInput={(val) => {
                    dispatch(
                      setRniData({
                        ...rni,
                        volumeInputP2: val
                      })
                    );
                  }}
                  key={1}
                  value={volumeInputP2}
                />
              ]}
            />
          </ArcContainer>
        )}

        <NavigationButtons
          nextDisabled={isNextDisabled()}
          nextRoute={'/rni/table'}
          nextVisible={true}
        />
      </BoxStyled>
    </>
  );
};

export { InputScreen };
